import type { StateCreator } from "zustand";
import type { CampaignProgressSchema, PublicCampaignSchema } from "@/types";
import { campaignKey, type CampaignKey } from "@/campaigns/campaign-key";
import type { AppStore } from "./app.store";

/** Subscribe lifecycle flags shared by the two type-agnostic streams. */
export interface CampaignStreamState {
  subscribed: boolean;
  loading: boolean;
}

/** A pending, unrevealed prize win — a presence marker, not the prize.
 *
 *  Deliberately carries no identity: since #3922 the server withholds prize titles until the
 *  player confirms the reveal, so there is nothing to name here. Three of this seam's readers
 *  only ever test presence (`key in unrevealedWins`) anyway; the two that render the reveal read
 *  the disclosed titles from the notifications slice instead.
 *
 *  Type-agnostic — any campaign type can have one. */
export interface CampaignWin {
  prizeImage?: string;
}

/**
 * The type-agnostic campaign layer, mirroring the backend split:
 *  - `campaignsById`   ← `subscribe:campaigns`         (structural, all types)
 *  - `joinedById`      ← `subscribe:campaign_progress` (per-player opt-in state)
 *  - `endsAtById`      ← `subscribe:campaign_progress` (the player's own end, laid over `end`)
 *
 * Every campaign type reads its structural + join data from here; only the
 * volatile, type-specific tail (prize-drop counts, tournament leaderboards)
 * lives in a per-type slice.
 */
export interface CampaignsSlice {
  campaignsById: Record<CampaignKey, PublicCampaignSchema>;
  joinedById: Record<CampaignKey, boolean>;
  /** When a campaign ends for this player, if not at its `end` — a limited-time campaign's
   *  window (`campaign_progress.ends_at`). Laid over `campaignsById[*].end`, so every countdown,
   *  sort and "ended" check reads the player's own end, as the legacy overlay did. */
  endsAtById: Record<CampaignKey, string>;
  /** The campaign currently being viewed, by `campaignKey` — type-agnostic
   *  (any campaign type). Lives here, not in a per-type slice. */
  selectedCampaignId: string | null;
  campaignsStream: CampaignStreamState;
  campaignProgressStream: CampaignStreamState;

  setCampaigns: (campaigns: PublicCampaignSchema[]) => void;
  setCampaignProgress: (progress: CampaignProgressSchema[]) => void;
  setSelectedCampaignId: (id: string | null) => void;
  /** Optimistic single-campaign opt-in write; the stream reconciles it on the
   *  next `campaign_progress` push. Type-agnostic — keyed by `campaignKey`. */
  setJoined: (key: CampaignKey, joined: boolean) => void;
  setCampaignsSubscribed: (subscribed: boolean) => void;
  setCampaignsLoading: (loading: boolean) => void;
  setCampaignProgressSubscribed: (subscribed: boolean) => void;
  setCampaignProgressLoading: (loading: boolean) => void;
  clearCampaigns: () => void;
  clearCampaignProgress: () => void;

  // Frontend-only "won a prize, pending reveal", keyed by campaign key (works for
  // any campaign type). Drives the in-game button's prize-reveal state. There is
  // no win stream yet, so this stays frontend-only for now.
  unrevealedWins: Record<CampaignKey, CampaignWin>;
  setUnrevealedWin: (key: CampaignKey, win: CampaignWin) => void;
  revealWin: (key: CampaignKey) => void;
}

/** The campaign as this player sees it: ending at their own end when they have one. */
const withEnd = (
  campaign: PublicCampaignSchema,
  end: string | undefined
): PublicCampaignSchema =>
  end && campaign.end !== end ? { ...campaign, end } : campaign;

const initialStream: CampaignStreamState = {
  subscribed: false,
  loading: false,
};

export const createCampaignsSlice: StateCreator<CampaignsSlice> = (set) => ({
  campaignsById: {},
  joinedById: {},
  endsAtById: {},
  selectedCampaignId: null,
  campaignsStream: initialStream,
  campaignProgressStream: initialStream,

  setSelectedCampaignId: (id) => set({ selectedCampaignId: id }),

  // The `campaigns` stream sends the full tile list on every push, so the map is
  // rebuilt wholesale — a campaign that dropped out of scope disappears.
  setCampaigns: (campaigns) =>
    set((state) => {
      const byId: Record<CampaignKey, PublicCampaignSchema> = {};
      for (const campaign of campaigns) {
        const key = campaignKey(campaign);
        byId[key] = withEnd(campaign, state.endsAtById[key]);
      }
      return { campaignsById: byId };
    }),

  setCampaignProgress: (progress) =>
    set((state) => {
      const joined: Record<CampaignKey, boolean> = {};
      const endsAt: Record<CampaignKey, string> = {};
      for (const p of progress) {
        const key = campaignKey(p);
        joined[key] = p.joined;
        if (p.ends_at) endsAt[key] = p.ends_at;
      }
      // Only the campaigns whose end actually moves get a new object, so a
      // push that changes nothing leaves `campaignsById` (and its readers) alone.
      let campaignsById = state.campaignsById;
      for (const [key, end] of Object.entries(endsAt)) {
        const campaign = campaignsById[key];
        if (!campaign || campaign.end === end) continue;
        if (campaignsById === state.campaignsById) {
          campaignsById = { ...campaignsById };
        }
        campaignsById[key] = withEnd(campaign, end);
      }
      return { joinedById: joined, endsAtById: endsAt, campaignsById };
    }),

  setJoined: (key, joined) =>
    set((state) => ({ joinedById: { ...state.joinedById, [key]: joined } })),

  setCampaignsSubscribed: (subscribed) =>
    set((state) => ({
      campaignsStream: { ...state.campaignsStream, subscribed, loading: false },
    })),
  setCampaignsLoading: (loading) =>
    set((state) => ({
      campaignsStream: { ...state.campaignsStream, loading },
    })),
  setCampaignProgressSubscribed: (subscribed) =>
    set((state) => ({
      campaignProgressStream: {
        ...state.campaignProgressStream,
        subscribed,
        loading: false,
      },
    })),
  setCampaignProgressLoading: (loading) =>
    set((state) => ({
      campaignProgressStream: { ...state.campaignProgressStream, loading },
    })),

  clearCampaigns: () =>
    set({
      campaignsById: {},
      campaignsStream: initialStream,
      selectedCampaignId: null,
    }),
  clearCampaignProgress: () =>
    set({
      joinedById: {},
      endsAtById: {},
      campaignProgressStream: initialStream,
    }),

  unrevealedWins: {},
  setUnrevealedWin: (key, win) =>
    set((state) => ({
      unrevealedWins: { ...state.unrevealedWins, [key]: win },
    })),
  revealWin: (key) =>
    set((state) => {
      if (!(key in state.unrevealedWins)) return state;
      const next = { ...state.unrevealedWins };
      delete next[key];
      return { unrevealedWins: next };
    }),
});

/** Whether the player has opted in to a campaign (by key). */
export function selectJoined(state: AppStore, key: CampaignKey): boolean {
  return state.joinedById[key] ?? false;
}
